import Phaser from 'phaser';

// Normal-map bake — the "each lit sprite carries a normal map" half of the
// lighting pipeline (Order 16.2). Phaser 4's Lighting component samples a Game
// Object's normal map from its texture's DATA SOURCE, so that's where we put it.
//
// The real NK rigs (Order 16.3) carry no authored normal maps — Character
// Animator puppets are flat art — so we derive one per part from the art itself.
//
// HOW: a part's ALPHA is its silhouette. Blur that silhouette and you get a
// height field that is tall down the middle of a limb and falls to zero at the
// edges — i.e. exactly the rounded, tube-like relief a hand-painted normal map
// for a cartoon limb would have. Sobel that height into a tangent-space normal.
// It costs one downscaled pass per part and it shapes to the actual drawing:
// the snout rounds like a snout, the ears round like ears, and the gap between
// the pig's legs reads as two separate rounded volumes rather than one slab.
//
// The alternative — the placeholder's `'sphere' | 'cylinder'` enum stamped over
// the part's bounding rect — cannot know where the art is inside that rect, so
// on real art it lights the empty corners as if they were body.
//
// Convention: tangent-space, +Z toward the viewer. Pixels outside the silhouette
// emit a flat (0,0,1) normal so unlit background stays neutral and the light only
// shapes the figure.

/** Green-channel (surface-up) sign. The MeshPlayer renders the mesh V-flipped, so
 *  the normal map is sampled flipped in Y too; this compensates so a light ABOVE
 *  the actor lights the TOP of the actor. */
const NORMAL_FLIP_Y = true;

/** How steeply height differences tilt the surface normal. Higher = more relief,
 *  so the moving light rakes across the body more dramatically. */
const RELIEF = 3.4;

/** Normal maps are sampled by UV, so they need no more resolution than the
 *  lighting can show. The pig's head layer is 4378×5445 — Sobelling that in JS
 *  at boot costs seconds and buys nothing. Cap the long edge. */
const NORMAL_MAX_DIM = 256;

/** Silhouette blur radius as a fraction of the part's short edge. This sets how
 *  "inflated" the part reads: bigger = rounder, softer, more tube-like. */
const ROUND_FRACTION = 0.16;

/** Height curve. < 1 pushes the dome outward so the falloff sits near the edge
 *  (a limb that is round most of the way across, not a soft mound). */
const DOME_EXPONENT = 0.62;

/** Bake a normal map for one part from its own artwork's alpha silhouette.
 *  Returns a canvas suitable for Texture.setDataSource. */
export function bakeAlphaNormalMap(source: HTMLImageElement | HTMLCanvasElement): HTMLCanvasElement {
  const sw = 'naturalWidth' in source ? source.naturalWidth : source.width;
  const sh = 'naturalHeight' in source ? source.naturalHeight : source.height;

  // Downscale the art into a working buffer; we only need its alpha.
  const k = Math.min(1, NORMAL_MAX_DIM / Math.max(sw, sh));
  const W = Math.max(2, Math.round(sw * k));
  const H = Math.max(2, Math.round(sh * k));

  const work = document.createElement('canvas');
  work.width = W;
  work.height = H;
  const wctx = work.getContext('2d', { willReadFrequently: true })!;
  wctx.drawImage(source, 0, 0, W, H);
  const src = wctx.getImageData(0, 0, W, H).data;

  // Silhouette mask (1 inside the art, 0 outside), kept for the flat-background
  // test after blurring smears the edge outward.
  const inside = new Uint8Array(W * H);
  const h = new Float32Array(W * H);
  for (let i = 0; i < W * H; i++) {
    const a = src[i * 4 + 3] / 255;
    inside[i] = a > 0.5 ? 1 : 0;
    h[i] = a;
  }

  // Blur the silhouette into a dome. Radius scales with the part's short edge so
  // a thin arm and a fat torso round by the same PROPORTION of their thickness.
  const radius = Math.max(1, Math.round(Math.min(W, H) * ROUND_FRACTION));
  boxBlur(h, W, H, radius, 3);

  // Renormalize so the tallest point of this part reaches 1, then shape the dome.
  let peak = 0;
  for (let i = 0; i < h.length; i++) if (h[i] > peak) peak = h[i];
  if (peak > 1e-6) for (let i = 0; i < h.length; i++) h[i] = Math.pow(h[i] / peak, DOME_EXPONENT);

  // Sobel height → normal.
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;
  const img = ctx.createImageData(W, H);
  const data = img.data;
  const at = (x: number, y: number) =>
    h[Math.max(0, Math.min(H - 1, y)) * W + Math.max(0, Math.min(W - 1, x))];

  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const i = (y * W + x) * 4;
      if (!inside[y * W + x]) {
        // Outside the art: flat. The diffuse alpha is 0 here anyway, but a
        // sloped normal in the margin would rim-light the cutout edge.
        data[i] = 128; data[i + 1] = 128; data[i + 2] = 255; data[i + 3] = 255;
        continue;
      }
      const dx = (at(x + 1, y) - at(x - 1, y)) * RELIEF;
      const dy = (at(x, y + 1) - at(x, y - 1)) * RELIEF;
      let nx = -dx;
      let ny = -dy;
      const nz = 1;
      if (NORMAL_FLIP_Y) ny = -ny;
      const inv = 1 / Math.hypot(nx, ny, nz);
      data[i] = (nx * inv * 0.5 + 0.5) * 255;
      data[i + 1] = (ny * inv * 0.5 + 0.5) * 255;
      data[i + 2] = (nz * inv * 0.5 + 0.5) * 255;
      data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

/** Attach a baked normal map to a diffuse texture as its data source, so any Game
 *  Object using that texture with setLighting(true) samples it as the normal map. */
export function attachNormalMap(scene: Phaser.Scene, textureKey: string, normal: HTMLCanvasElement): void {
  const tex = scene.textures.get(textureKey);
  // renew=true forces the GL upload of the new data-source image.
  tex.setDataSource(normal, 0, true, normal.width, normal.height);
}

/** Derive and attach a normal map for every part texture of a loaded actor. */
export function attachActorNormalMaps(scene: Phaser.Scene, textureKeys: readonly string[]): void {
  for (const key of textureKeys) {
    const tex = scene.textures.get(key);
    const img = tex.getSourceImage();
    if (!(img instanceof HTMLImageElement) && !(img instanceof HTMLCanvasElement)) continue;
    attachNormalMap(scene, key, bakeAlphaNormalMap(img));
  }
}

/** In-place box blur, `passes` iterations of a separable radius-r box. Three
 *  passes of a box approximate a Gaussian closely enough for a height field. */
function boxBlur(h: Float32Array, W: number, H: number, radius: number, passes: number): void {
  const tmp = new Float32Array(h.length);
  const win = radius * 2 + 1;
  for (let p = 0; p < passes; p++) {
    // Horizontal: running sum across each row.
    for (let y = 0; y < H; y++) {
      const row = y * W;
      let sum = 0;
      for (let x = -radius; x <= radius; x++) sum += h[row + clamp(x, W)];
      for (let x = 0; x < W; x++) {
        tmp[row + x] = sum / win;
        sum += h[row + clamp(x + radius + 1, W)] - h[row + clamp(x - radius, W)];
      }
    }
    // Vertical: running sum down each column.
    for (let x = 0; x < W; x++) {
      let sum = 0;
      for (let y = -radius; y <= radius; y++) sum += tmp[clamp(y, H) * W + x];
      for (let y = 0; y < H; y++) {
        h[y * W + x] = sum / win;
        sum += tmp[clamp(y + radius + 1, H) * W + x] - tmp[clamp(y - radius, H) * W + x];
      }
    }
  }
}

const clamp = (v: number, n: number) => (v < 0 ? 0 : v >= n ? n - 1 : v);
